import { getPerformance, trace } from "firebase/performance";
import { getApps } from "firebase/app";
import { createPersistedState } from "~/utils/persistedState";

interface fetchResult {
  id: number;
  name: string;
}



// grouping dropdown by make
export interface modelFetchResult {
  id: number,
  make: string,
  models: fetchResults[]
}
export interface SearchResult {
  totalCount: number;
  announcements: announceById[];
  filters: UserSearchInput,
}

export type fetchResults = fetchResult[] | undefined[];

interface searchState {
  // models porperties
  models?: modelFetchResult[];
  modelsIds?: number[];
  modelsName?: string[];
  // makes porperties
  makes?: fetchResults;
  makesName?: string[];
  makesIds?: number[];
  //-----------
  allAnnounces: SearchResult | undefined;
  searchResult: SearchResult | undefined;
  promotedSearchResult: announceById[];
  announceById: announceById;
  userInput: UserSearchInput;
  selectedInput: UserSearchInput;
  userSearchInputCount: number;
  announceCount: SearchResult | undefined;
  stopFetchScroll: boolean
  pendingStats: {
    announceCountPending: boolean
    saveSearch: boolean
    fetchResult: boolean
    fetchResultOnScroll: boolean
  }
}

const capitalizeFirst = (value: unknown) => {
  if (typeof value !== "string") return value;
  const normalized = value.trim();
  if (!normalized) return "";
  return normalized.charAt(0).toLocaleUpperCase("fr-FR") + normalized.slice(1);
};

const normalizeFlatOptions = (source: unknown) => {
  const raw = Array.isArray(source)
    ? source
    : source && typeof source === "object"
      ? [source]
      : [];

  return raw
    .filter((item: any) => item && typeof item === "object")
    .map((item: any) => ({
      ...item,
      id: Number(item.id),
      label: typeof item.label === "string" ? item.label : "",
      checked: item?.checked !== false,
    }))
    .filter((item: any) => Number.isInteger(item.id) && item.label.trim().length > 0);
};

const normalizeGroupedOptions = (source: unknown) => {
  const rawGroups = Array.isArray(source) ? source : [];

  return rawGroups
    .filter((group: any) => group && typeof group === "object")
    .map((group: any) => ({
      ...group,
      parent: typeof group.parent === "string" ? group.parent : "",
      items: normalizeFlatOptions(group.items),
    }))
    .filter((group: any) => group.items.length > 0);
};

const mergeGroupsByParent = (groups: Array<{ parent: string; items: any[] }>) => {
  const merged = new Map<string, { parent: string; items: any[] }>();

  for (const group of groups) {
    const parentLabel = typeof group.parent === "string" && group.parent.trim().length > 0
      ? group.parent.trim()
      : "Options";
    const key = parentLabel.toLocaleLowerCase("fr-FR");
    const existing = merged.get(key);

    if (!existing) {
      merged.set(key, {
        parent: parentLabel,
        items: [...(Array.isArray(group.items) ? group.items : [])],
      });
      continue;
    }

    const seenItemKeys = new Set(
      existing.items.map((item: any) => `${Number(item?.id)}::${String(item?.label ?? "")}`)
    );
    for (const item of Array.isArray(group.items) ? group.items : []) {
      const itemKey = `${Number(item?.id)}::${String(item?.label ?? "")}`;
      if (seenItemKeys.has(itemKey)) continue;
      seenItemKeys.add(itemKey);
      existing.items.push(item);
    }
  }

  return Array.from(merged.values());
};

const resolveGroupedOptionsSource = (announce: announceById) => {
  const aiOptions = normalizeFlatOptions((announce as any)?.optionsAi);
  const groupedOptionsAi = normalizeGroupedOptions((announce as any)?.groupedOptionsAi);
  if (aiOptions.length > 0) {
    return groupedOptionsAi;
  }

  const groupedOptions = normalizeGroupedOptions((announce as any)?.groupedOptions);
  const groupedCarOptions = normalizeGroupedOptions((announce as any)?.groupedCarOptions);
  return mergeGroupsByParent([...groupedOptions, ...groupedCarOptions]);
};

const isActiveFilterValue = (key: string, value: any): boolean => {
  if (key === "front") return false;
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value === true;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const countActiveFilters = (input: Record<string, any> | undefined): number => {
  if (!input || typeof input !== "object") return 0;

  let count = 0;
  for (const [key, value] of Object.entries(input)) {
    if (!isActiveFilterValue(key, value)) continue;
    if (Array.isArray(value)) {
      count += value.length;
      continue;
    }
    count += 1;
  }
  return count;
};

const state = (): searchState => ({
  // models porperties
  models: [],
  modelsIds: [],
  modelsName: [],
  // makes porperties
  makes: [],
  makesIds: [],
  makesName: [],
  //-----------
  allAnnounces: undefined,
  searchResult: {} as SearchResult,
  promotedSearchResult: [],
  announceById: {} as announceById,
  userInput: {} as UserSearchInput,
  selectedInput: {} as UserSearchInput,
  userSearchInputCount: 0,
  announceCount: {} as SearchResult,
  stopFetchScroll: false,
  pendingStats: {
    announceCountPending: false,
    saveSearch: false,
    fetchResult: false,
    fetchResultOnScroll: false
  }
});

const getters = {
  getUserModelsId: (state: searchState) => {

    if (!state.selectedInput.models) {
      state.modelsIds = []
      return state.modelsIds
    }

    state.modelsIds = state.selectedInput.models.map((model: any) => model.id)

    return state.modelsIds
  },
  getUserMakesId: (state: searchState) => {

    // reset model array if make is empty
    if (!state.selectedInput.makes) {
      state.selectedInput.models = [];
      state.makesIds = [];
      return state.makesIds;
    }
    // capture makes ids

    state.makesIds = state.selectedInput.makes.map((model: any) => model.id)


    return state.makesIds
  },
  getMakes: (state: searchState) => {
    return state.makes;
  },
  getModels: (state: searchState) => {
    return state.models;
  },
  getUserInput: (state: searchState) => {
    return state.userInput;
  },
  getSelectedInput: (state: searchState) => {
    return state.selectedInput;
  },

  getAnnounceById: (state: searchState) => {
    return state.announceById;
  },
  groupedOptions: (_state: searchState) => {

    return (announce: announceById) => resolveGroupedOptionsSource(announce).map((obj: any) => ({
      ...obj,
      parent: capitalizeFirst(obj.parent),
      items: (Array.isArray(obj.items) ? obj.items : [])
        .filter((item: any) => item?.checked === true)
        .map((item: any) => ({
          ...item,
          label: capitalizeFirst(item.label)
        }))
    })).filter((obj: any) => obj.items.length > 0)

  },
  groupedCarOptions: (_state: searchState) => {

    return (_announce: announceById) => []

  },
  getFetchResult: (state: searchState) => {
    return state.searchResult;
  },
  getPromotedSearchResult: (state: searchState) => {
    return state.promotedSearchResult;
  },
  getAllAnnounces: (state: searchState) => {
    return state.allAnnounces?.announcements;
  },
  getIsFreshPage: (state: searchState) => {
    return Object.keys(state.userInput).length == 0;
  },
  getAnnounceCount: (state: searchState) => {


    return state.announceCount?.totalCount
  },
  getFilterCounte: (state: searchState) => {
    return countActiveFilters(state.userInput as Record<string, any>);
  },
  getSelectedFilterCounte: (state: searchState) => {
    const mergedFilters = {
      ...(state.userInput as Record<string, any>),
      ...(state.selectedInput as Record<string, any>),
    };
    return countActiveFilters(mergedFilters);
  },
  getPendingStats: (state: searchState) => {
    return state.pendingStats
  },
};

export const useSearchStore = defineStore("searchStore", {
  state,
  getters,

  actions: {

    setAnnounceCount(count: any) {
      if (!this.announceCount || typeof this.announceCount !== 'object') {
        this.announceCount = { totalCount: count, announcements: [], filters: {} as UserSearchInput };
      } else {
        this.announceCount.totalCount = count;
      }
    },

    async collectUserInput(values: UserSearchInput) {
      // console.log(this.userInput);
      // console.log( values);
      if (this.userInput === values) return
      this.userInput = { ...values };
      this.selectedInput = { ...values };
    },
    resetStoreValues() {

      this.models = []
      this.modelsIds = []
      this.modelsName = []
      this.makesIds = []
      this.makesName = []
      this.userInput = {} as UserSearchInput
      this.selectedInput = {} as UserSearchInput
      this.promotedSearchResult = []
    },
    setPromotedSearchResult(data: announceById[]) {
      this.promotedSearchResult = Array.isArray(data) ? [...data] : [];
    },
    appendPromotedSearchResult(data: announceById[]) {
      if (!Array.isArray(data) || !data.length) {
        return;
      }

      const existingIds = new Set(
        this.promotedSearchResult.map((item: any) => String(item?.id))
      );
      const appended = data.filter((item: any) => {
        const id = String(item?.id);
        if (existingIds.has(id)) return false;
        existingIds.add(id);
        return true;
      });

      if (appended.length) {
        this.promotedSearchResult = [
          ...this.promotedSearchResult,
          ...appended,
        ];
      }
    },
    clearPromotedSearchResult() {
      this.promotedSearchResult = [];
    },
    async fetchMarks() {
      const runTimeConfig = useRuntimeConfig();
      const response: any = await $fetch("/makes/all", {
        method: "GET",
        headers: {
          token: runTimeConfig.public.token,
        },
        baseURL: runTimeConfig.public.baseUrl,
      });


      this.makes = response;
    },
    async fetchModels(makesIdss: number[] | undefined) {
      const runTimeConfig = useRuntimeConfig();
      this.models = [];
      const response: any = await $fetch("/models/find-by-many-makes-ids", {
        method: "POST",
        headers: {
          token: runTimeConfig.public.token,
        },
        body: {
          makesIds: makesIdss,
        },
        baseURL: runTimeConfig.public.baseUrl,
      });


      this.models = response;

    },

    filterUserSearchInput(input: UserSearchInput): Partial<UserSearchInput> {
      const isValueAvailable = (value: any): boolean => {
        if (value === undefined || value === null) {
          return false;
        }
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (typeof value === 'string') {
          return value.trim().length > 0;
        }
        if (typeof value === 'object') {
          return Object.keys(value).length > 0;
        }
        // Numbers and booleans are always valid unless null
        return true;
      };

      return Object.fromEntries(
        Object.entries(input).filter(([_, value]) => isValueAvailable(value))
      );
    },
    async fetchAnnounce(pageNumber: number, userId: number = 0, sortBy?: string) {
      let obj: any = {}
      if (this.getUserMakesId.length) {
        obj.makesIds = toRaw(this.getUserMakesId)
      }
      if (this.getUserModelsId.length) {
        obj.modelsIds = toRaw(this.getUserModelsId)
      }
      obj = { ...obj, ...this.filterUserSearchInput(this.userInput) }

      const cookieId = useCookie("userId");
      if (!userId) {
        userId = Number(cookieId.value)
      }
      //const { sortBy } = useRoute().query

      const runTimeConfig = useRuntimeConfig();
      this.pendingStats.fetchResult = true;

      let fetchTrace: any = null;
      try {
        if (getApps().length > 0) {
          fetchTrace = trace(getPerformance(), 'fetchAnnounce');
          fetchTrace.start();
        }
      } catch (error) {
        console.warn('Firebase Performance not available:', error);
      }

      try {
        const response: SearchResult = await $fetch(
          "/announcements/search-result",
          {
            method: "POST",
            headers: {
              token: runTimeConfig.public.token,
            },
            body: {
              ...{
                sortBy: sortBy,
                userId: userId,
                pageNumber: pageNumber
              }, ...obj
            },
            baseURL: runTimeConfig.public.baseUrl,
          }
        );



        if (pageNumber === 1) {
          this.searchResult = response
          return
        }
        if (!this.searchResult || !Array.isArray(this.searchResult.announcements)) {
          this.searchResult = response;
          return;
        }

        const existingIds = new Set(
          this.searchResult.announcements.map((item: any) => String(item?.id))
        );
        const nextAnnouncements = response.announcements.filter((item: any) => {
          const id = String(item?.id);
          if (existingIds.has(id)) return false;
          existingIds.add(id);
          return true;
        });

        this.searchResult.announcements = [
          ...this.searchResult.announcements,
          ...nextAnnouncements,
        ];



      } catch (error) {
        throw new Error(String(error))
      } finally {
        this.pendingStats.fetchResult = false
        if (fetchTrace) {
          fetchTrace.stop();
        }
      }
    },

    async fetchAllAnnounce() {
      const runTimeConfig = useRuntimeConfig();

      let fetchTrace: any = null;
      if (getApps().length > 0) {
        fetchTrace = trace(getPerformance(), 'fetchAllAnnounce');
        fetchTrace.start();
      }

      try {
        const response: SearchResult = await $fetch("/announcements/all", {
          method: "POST",
          headers: {
            token: runTimeConfig.public.token,
          },
          baseURL: runTimeConfig.public.baseUrl,
        });
        this.allAnnounces = response;
      } catch (error) {
        throw new Error(String(error))
      } finally {
        if (fetchTrace) {
          fetchTrace.stop();
        }
      }
    },
    async searchCount() {
      const runTimeConfig = useRuntimeConfig();
      this.pendingStats.announceCountPending = true;

      let fetchTrace: any = null;
      if (getApps().length > 0) {
        fetchTrace = trace(getPerformance(), 'searchCount');
        fetchTrace.start();
      }

      try {
        const response: SearchResult | undefined = await $fetch(
          "/announcements/search-count",
          {
            method: "POST",
            headers: {
              token: runTimeConfig.public.token,
            },
            body: {
              makesIds: toRaw(this.getUserMakesId),
              modelsIds: toRaw(this.getUserModelsId),
              ...toRaw(this.selectedInput),
            },
            baseURL: runTimeConfig.public.baseUrl,
          }
        );
        this.announceCount = response

      } catch (error) {
        throw new Error(String(error))

      } finally {
        this.pendingStats.announceCountPending = false
        if (fetchTrace) {
          fetchTrace.stop();
        }
      }
    },

    async fetchAnnounceByCategorie(categories: string) {

      const runTimeConfig = useRuntimeConfig();
      const response: SearchResult = await $fetch(
        "/announcements/search-result",
        {
          method: "POST",
          headers: {
            token: runTimeConfig.public.token,
          },
          body: {
            categories: categories,
          },
          baseURL: runTimeConfig.public.baseUrl,
        }
      );
      this.searchResult = response;
    },
    async fetchAnnounceByMakes(makes: { id: number; name: string; }) {
      const { sortBy } = useRoute().query

      const runTimeConfig = useRuntimeConfig();
      const response: SearchResult = await $fetch(
        "/announcements/search-result",
        {
          method: "POST",
          headers: {
            token: runTimeConfig.public.token,
          },
          body: {
            makesIds: [makes.id],
            sortBy: sortBy,
            pageNumber: 1
          },
          baseURL: runTimeConfig.public.baseUrl,
        }
      );
      this.searchResult = response;
    },
    async fetchAnnounceById(announceById: string | string[]) {

      const runTimeConfig = useRuntimeConfig();
      const cookieUserId = useCookie("userId");


      const response: any = await $fetch(`/announcements/find/${announceById}/${cookieUserId}`, {
        method: "GET",
        headers: {
          token: runTimeConfig.public.token,
        },
        baseURL: runTimeConfig.public.baseUrl,
      });

      this.announceById = response;
    },

    async saveSearchFilter() {

      const runTimeConfig = useRuntimeConfig();
      const cookie = useCookie("userToken");
      this.pendingStats.saveSearch = true;

      // font requirement 
      const front: { [key: string]: undefined | number | string | string[] } = {
        "Année": this.userInput.fromRegYear ? "de " + this.userInput.fromRegYear : 'Toutes les années',
        "Transmission": this.userInput.transmissions ? this.userInput.transmissions : 'Toutes les transmissions',
        "Kilométrage": this.userInput.minKilometers ? `de ${usePriceFormat(this.userInput.minKilometers)} Km` + (this.userInput.maxKilometers ? ` a ${usePriceFormat(this.userInput.maxKilometers)} Km` : '') : "Tout",
        "Prix": this.userInput.minPrice ? `de ${usePriceFormat(this.userInput.minPrice)} DT` + (this.userInput.maxPrice ? ` a ${usePriceFormat(this.userInput.minPrice)} DT` : '') : 'Tous les prix',
        "Type de carrosserie": this.userInput.categories && this.userInput.categories.length ? this.userInput.categories : 'Tous les type de carrosseries',
        "Type de carburant": this.userInput.fuelTypes ? this.userInput.fuelTypes : 'Tout les types de carburant',
      }

      let fetchTrace: any = null;
      if (getApps().length > 0) {
        fetchTrace = trace(getPerformance(), 'saveSearchFilter');
        fetchTrace.start();
      }
      front.count = Object.keys(front).length;
      // 
      try {
        await $fetch(
          "/researches/save",
          {
            method: "POST",
            headers: {
              Authorization: "Bearer " + cookie.value,
              "Content-Type": "application/json"
            },
            body: {
              makesIds: toRaw(this.getUserMakesId),
              modelsIds: toRaw(this.getUserModelsId),
              ...toRaw(this.userInput),
              front: front
            },
            baseURL: runTimeConfig.public.baseUrl,
          }
        );

      } catch (error) {
        // console.log(error);
        throw new Error(String(error))

      } finally {
        this.pendingStats.saveSearch = false
        if (fetchTrace) {
          fetchTrace.stop();
        }
      }

    },
    async applyUserSavedSearch(id: number) {
      const runTimeConfig = useRuntimeConfig();
      const cookie = useCookie("userToken");


      try {
        const response = await $fetch<SearchResult>("/researches/result/" + id, {
          method: "GET",
          headers: {
            Authorization: "Bearer " + cookie.value,
          },
          baseURL: runTimeConfig.public.baseUrl,

        });
        this.userInput = toReactive(response.filters);
        this.searchResult = response;
        return response.filters

      } catch (error) {
        // console.log(error);
        throw new Error(String(error))
      }
    },
  },
  persist: createPersistedState({
    key: 'topauto:search',
    version: 3,
    migrate: (state) => ({
      ...state,
      promotedSearchResult: Array.isArray((state as any)?.promotedSearchResult)
        ? (state as any).promotedSearchResult
        : [],
    }),
    paths: [
      'models',
      'modelsIds',
      'modelsName',
      'makes',
      'makesIds',
      'makesName',
      'searchResult',
      'promotedSearchResult',
      'userInput',
      'selectedInput',
      'userSearchInputCount',
      'announceCount',
      'stopFetchScroll'
    ]
  })
});
